import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  ArrowRight, Award, CalendarCheck, Check, ChevronDown, ChevronUp, CircleDashed, Flag, Layers, LayoutGrid, MapPin,
  Play, Plus, Repeat2, Route, Search, Sparkles,
} from 'lucide-react';
import { apiErrorMessage, authService, trailService } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { Button, EmptyState, GlobalShell, Loader, Modal, Field, PageHeader } from '../components/ui';
import {
  AvailabilityModal, SealPill, StatusPill, TypePill, WeightBar, WeightChip,
} from '../components/trails/TrailBits';
import { SEALS, TRAIL_TYPES, fmtHoursRound } from '../utils/trails';
import './Trails.css';

const FILTERS = [
  { key: 'all', label: 'Todas', icon: LayoutGrid },
  { key: 'certification', label: 'Certificação', icon: TRAIL_TYPES.certification.icon },
  { key: 'career', label: 'Carreira', icon: TRAIL_TYPES.career.icon },
  { key: 'project', label: 'Projeto', icon: TRAIL_TYPES.project.icon },
];

const MAX_AREAS = 4;

/** Catálogo de trilhas; na primeira visita, com a apresentação compacta. */
function Trails() {
  const navigate = useNavigate();
  const { user, setUser, isAdmin } = useAuth();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [q, setQ] = useState('');
  const [query, setQuery] = useState('');
  const [params] = useSearchParams();
  const [type, setType] = useState(() => (TRAIL_TYPES[params.get('tipo')] ? params.get('tipo') : 'all'));
  const [introOpen, setIntroOpen] = useState(null);
  const [enrollFor, setEnrollFor] = useState(null);
  const [creating, setCreating] = useState(false);

  const load = useCallback(() => {
    setError(null);
    trailService.catalog({ q: query || undefined, type })
      .then(setData)
      .catch(err => setError(apiErrorMessage(err, 'Não foi possível carregar as trilhas.')));
  }, [query, type]);

  useEffect(() => { load(); }, [load]);

  const hasActive = Boolean(data?.today);
  // Apresentação: enquanto não foi escondida e o aluno não tem trilha ativa; o link reabre
  const showIntro = introOpen ?? (!user?.trailsIntroSeen && !hasActive);

  const hideIntro = async () => {
    setIntroOpen(false);
    try {
      setUser(await authService.updateOnboarding({ trailsIntroSeen: true }));
    } catch {
      // Só a preferência de não mostrar de novo
    }
  };

  const markHowSeen = async () => {
    try {
      setUser(await authService.updateOnboarding({ trailsHowSeen: true }));
    } catch {
      // Só tira o destaque da pílula
    }
  };

  const example = data?.trails.find(t => t.hasStages) || data?.trails[0];
  const open = (t) => navigate(`/trilhas/${t.slug}`);

  const search = (e) => {
    e.preventDefault();
    setQuery(q.trim());
  };

  return (
    <GlobalShell>
      <div className="ss-page ss-page--wide trc-page">
        <PageHeader
          icon={Route}
          eyebrow="Caminhos guiados"
          title={(
            <>
              Trilhas
              {!showIntro && (
                <button type="button" className="trc-whatis" onClick={() => setIntroOpen(true)} data-tour="trilhas-intro">O que é uma trilha?</button>
              )}
            </>
          )}
          subtitle="Do ponto de partida até um destino verificável: uma certificação, um cargo ou um projeto entregue."
          actions={isAdmin && <Button variant="outline" icon={Plus} onClick={() => setCreating(true)} data-tour="nova-trilha">Nova trilha</Button>}
        />

        {showIntro && (
          <Intro
            example={example}
            howSeen={Boolean(user?.trailsHowSeen)}
            onExample={() => example && navigate(`/trilhas/${example.slug}`)}
            onHide={hideIntro}
            onHowSeen={markHowSeen}
          />
        )}

        {!showIntro && data?.today && <TodayStrip today={data.today} onOpen={() => navigate(`/trilhas/${data.today.slug}/plano`)} />}

        <div className="trc-toolbar">
          <form className="trc-search" onSubmit={search} role="search">
            <Search size={16} aria-hidden="true" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Buscar por certificação, código do exame, cargo ou tecnologia..."
              aria-label="Buscar trilhas"
              enterKeyHint="search"
            />
          </form>
          <div className="ss-seg trc-filters" role="tablist" aria-label="Tipo de trilha" data-tour="trilhas-tipos">
            {FILTERS.map(f => {
              const count = data?.counts?.[f.key] ?? 0;
              return (
                <button
                  key={f.key}
                  type="button"
                  role="tab"
                  aria-selected={type === f.key}
                  className={`ss-seg__item ${type === f.key ? 'is-active' : ''} ${data && count === 0 ? 'is-empty' : ''}`}
                  onClick={() => setType(f.key)}
                >
                  <f.icon size={13} aria-hidden="true" />
                  <span>{f.label}</span>
                  <span className="ss-seg__count">{count}</span>
                </button>
              );
            })}
          </div>
        </div>

        {error && <EmptyState file="trilhas.xml" title="Não foi possível carregar as trilhas" description={error} action={<Button onClick={load}>Tentar de novo</Button>} />}
        {!error && !data && <Loader label="Carregando trilhas..." />}

        {!error && data && (
          data.trails.length === 0 ? (
            <EmptyState
              file="trilhas.xml"
              title={query ? 'Nenhuma trilha encontrada' : 'Ainda não há trilhas'}
              description={query ? 'Tente outro termo: o código do exame, a tecnologia ou o cargo.' : 'As trilhas aparecem aqui quando forem publicadas.'}
            />
          ) : (
            <div className="trc-cards">
              {data.trails.map((t, i) => (
                <TrailCard key={t.id} trail={t} tour={i === 0 ? 'trilha-card' : undefined} onOpen={() => open(t)} onStart={() => setEnrollFor(t)} />
              ))}
            </div>
          )
        )}
      </div>

      <AvailabilityModal
        open={Boolean(enrollFor)}
        trail={enrollFor}
        onClose={() => setEnrollFor(null)}
        onDone={() => navigate(`/trilhas/${enrollFor.slug}`)}
      />
      <CreateTrailModal open={creating} onClose={() => setCreating(false)} onCreated={(slug) => navigate(`/trilhas/${slug}/editar`)} />
    </GlobalShell>
  );
}

/** Visita recorrente: faixa do plano de hoje. */
function TodayStrip({ today, onOpen }) {
  const stage = today.stageNumber ? `Etapa ${today.stageNumber}` : today.title;
  const detail = [today.trailTitle, stage, today.minutes ? fmtHoursRound(today.minutes) : null].filter(Boolean).join(' · ');
  return (
    <section className="trc-today">
      <CalendarCheck size={17} className="trc-today__icon" aria-hidden="true" />
      <span className="trc-today__text" title={[today.title, today.subtitle].filter(Boolean).join(' · ')}>
        <strong>Plano de hoje · Dia {today.day} de {today.totalDays}</strong>
        {detail && <span> · {detail}</span>}
      </span>
      <Button variant="accent" size="sm" icon={Play} onClick={onOpen}>Abrir plano</Button>
    </section>
  );
}

/** Card da trilha: tipo, código, título, 4 números, pesos ou áreas, e rodapé com nota e CTA. */
function TrailCard({ trail: t, tour, onOpen, onStart }) {
  const enrolled = t.enrollment;
  const stats = t.hasStages
    ? [
      { v: t.questions, l: 'questões' },
      { v: t.passPct != null ? `${t.passPct}%` : null, l: 'nota de corte', tone: 'lime' },
      { v: t.days || null, l: 'dias de plano' },
      { v: t.totalMinutes ? fmtHoursRound(t.totalMinutes) : null, l: 'de estudo' },
    ]
    : [
      { v: t.questions, l: 'questões' },
      { v: t.passPct != null ? `${t.passPct}%` : null, l: 'nota de corte', tone: 'lime' },
      { v: t.areas.length || null, l: 'áreas' },
      { v: null, l: 'sem plano ainda', tone: 'muted' },
    ];
  const extraAreas = t.areas.length - MAX_AREAS;
  return (
    <article className="trc-card" data-tour={tour}>
      <div className="trc-card__top">
        <TypePill type={t.type} />
        {t.examCode && <span className="trc-card__code">{t.examCode}</span>}
        {t.status === 'draft' && <StatusPill status="pending" label="Rascunho" />}
        {enrolled && <span className="trc-card__enrolled"><Check size={13} strokeWidth={2.8} aria-hidden="true" />Inscrito</span>}
      </div>
      <div className="trc-card__head">
        <button type="button" className="trc-card__title" onClick={onOpen}>{t.title}</button>
        {t.description && <p className="trc-card__desc">{t.description}</p>}
      </div>
      <div className="trc-card__stats">
        {stats.map(s => (
          <span key={s.l} className={s.tone ? `is-${s.tone}` : ''}>
            <strong>{s.v ?? '—'}</strong>
            <span>{s.l}</span>
          </span>
        ))}
      </div>
      {t.hasStages && t.weights.length > 0 && (
        <div className="trc-card__weights">
          <div className="trc-card__wlabel">
            <span>{t.weights.length} tópicos do exame por peso · {t.stageCount} {t.stageCount === 1 ? 'etapa' : 'etapas'}</span>
            <span>cor = etapa</span>
          </div>
          <WeightBar weights={t.weights} />
        </div>
      )}
      {!t.hasStages && t.areas.length > 0 && (
        <div className="trc-card__weights">
          <span className="trc-card__wlabel">Áreas do exame · ainda sem etapas</span>
          <div className="trc-card__areas">
            {t.areas.slice(0, MAX_AREAS).map(a => <span key={a}>{a}</span>)}
            {extraAreas > 0 && <span className="is-more" title={t.areas.slice(MAX_AREAS).join(', ')}>+{extraAreas}</span>}
          </div>
        </div>
      )}
      <div className="trc-card__foot">
        {enrolled ? (
          <div className="trc-card__progress">
            <span className="trc-card__plabel">
              <span>Dia {enrolled.day} de {enrolled.totalDays}{enrolled.examLabel ? ` · prova em ${enrolled.examLabel}` : ''}</span>
              <strong>{enrolled.pct}%</strong>
            </span>
            <span className="trc-card__bar"><span style={{ width: `${enrolled.pct}%` }} /></span>
          </div>
        ) : (
          <span className="trc-card__hint">{t.hint}</span>
        )}
        {enrolled
          ? <Button icon={ArrowRight} onClick={onOpen}>Continuar</Button>
          : t.hasStages
            ? <Button icon={ArrowRight} onClick={onStart}>Começar trilha</Button>
            : <Button variant="outline" icon={ArrowRight} onClick={onOpen}>Ver trilha</Button>}
      </div>
    </article>
  );
}

const PATH = [
  { icon: MapPin, title: 'Ponto de partida', text: 'Sessões e materiais de base' },
  { icon: Layers, title: 'Etapas', text: 'Tópicos do exame com o peso de cada um' },
  { icon: Repeat2, title: 'Reta final', text: 'Revisão e simulados' },
  { icon: Flag, title: 'Dia da prova', text: 'Checklist, ritmo e relatos', tone: 'lime' },
  { icon: Award, title: 'Depois', text: 'Badge e a próxima trilha', tone: 'ring' },
];

// Mini-gráfico do plano: horas por dia, na cor de cada etapa (ilustrativo)
const MINI_BARS = [3, 8, 8, 8, 12, 12, 12, 16, 16, 16, 8, 6, 6, 5]
  .map((h, i) => [h, i < 4 ? 'var(--trc-e2)' : i < 7 ? 'var(--trc-e3)' : i < 10 ? 'var(--trc-e4)' : 'var(--trc-final)']);

/** Pausa as animações quando o elemento sai da tela. */
function useOnScreen(ref, enabled) {
  const [visible, setVisible] = useState(true);
  useEffect(() => {
    if (!enabled || !ref.current || typeof IntersectionObserver === 'undefined') return undefined;
    const io = new IntersectionObserver(([entry]) => setVisible(entry.isIntersecting));
    io.observe(ref.current);
    return () => io.disconnect();
  }, [ref, enabled]);
  return visible;
}

/** Apresentação compacta: o que é, o caminho, os tipos; etapas, plano e selos sob demanda. */
function Intro({ example, howSeen, onExample, onHide, onHowSeen }) {
  const ref = useRef(null);
  const [howOpen, setHowOpen] = useState(false);
  const [seen, setSeen] = useState(howSeen);
  useEffect(() => { if (howSeen) setSeen(true); }, [howSeen]);
  // Efeitos de destaque só até a pessoa abrir e dar "Entendi"
  const fx = !seen && !howOpen;
  const visible = useOnScreen(ref, fx);

  const understood = () => {
    setHowOpen(false);
    setSeen(true);
    onHowSeen();
  };

  return (
    <section ref={ref} className={`trc-intro ${fx ? 'has-fx' : ''} ${fx && !visible ? 'is-paused' : ''}`}>
      <div className="trc-intro__body">
        <div className="trc-intro__left">
          <span className="trc-intro__eyebrow">O que é uma trilha</span>
          <h2 className="trc-intro__title"><span>Um caminho</span> com chegada.</h2>
          <p className="trc-intro__lead">
            Diz o que estudar, em que ordem, quantas horas por dia e como saber se você está pronto.
          </p>
          <div className="trc-intro__actions">
            <Button variant="accent" icon={Play} onClick={onExample} disabled={!example}>Ver trilha de exemplo</Button>
            <button type="button" className="trc-intro__hide" onClick={onHide}>Entendi, esconder</button>
          </div>
        </div>
        <div className="trc-intro__right">
          <ol className="trc-path">
            {PATH.map((p, i) => (
              <li key={p.title} className={`trc-path__node ${p.tone ? `trc-path__node--${p.tone}` : ''}`} style={{ '--i': i }}>
                {i < PATH.length - 1 && <span className="trc-path__line" aria-hidden="true"><span className="trc-path__beam" /></span>}
                <span className="trc-path__dot"><p.icon size={17} aria-hidden="true" /></span>
                <strong>{p.title}</strong>
                <span>{p.text}</span>
              </li>
            ))}
          </ol>
          <div className="trc-intro__types">
            <span className="trc-intro__eyebrow">Tipos de trilha</span>
            <div className="trc-kinds">
              {Object.entries(TRAIL_TYPES).map(([k, t]) => (
                <div key={k} className="trc-kind">
                  <span className="trc-kind__icon"><t.icon size={16} aria-hidden="true" /></span>
                  <span><strong>{t.label}</strong><span>{t.short}</span></span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="trc-how">
        <div className="trc-how__bar" data-tour="trilhas-como">
          <button type="button" className="trc-how__pill" onClick={() => setHowOpen(o => !o)} aria-expanded={howOpen} aria-controls="trc-how-panel">
            <span className="trc-how__spark"><Sparkles size={12} aria-hidden="true" /></span>
            <span className="trc-how__text">Como funcionam etapas, plano e selos</span>
          </button>
          {howOpen && (
            <button type="button" className="trc-how__ok" onClick={understood}>
              <Check size={13} aria-hidden="true" />Entendi
            </button>
          )}
          <button type="button" className="trc-how__toggle" onClick={() => setHowOpen(o => !o)} aria-expanded={howOpen} aria-controls="trc-how-panel">
            {howOpen ? 'Recolher' : 'Ver detalhes'}
            {howOpen ? <ChevronUp size={14} aria-hidden="true" /> : <ChevronDown size={14} aria-hidden="true" />}
          </button>
        </div>
        {howOpen && (
          <div className="trc-how__panel" id="trc-how-panel">
            <div className="trc-explain">
              <section className="trc-explain__card">
                <span className="trc-explain__num">01</span>
                <h3>Cada etapa mostra o que vale</h3>
                <p>Os tópicos do exame aparecem com o peso oficial, para você saber onde investir mais tempo.</p>
                <div className="trc-explain__row"><WeightChip name="Platform Basics" label="11–20%" /><WeightChip name="PCM" label="≤10%" /></div>
              </section>
              <section className="trc-explain__card">
                <span className="trc-explain__num">02</span>
                <h3>Estudar, praticar e provar</h3>
                <p>Toda etapa termina num checkpoint. O status mostra se você está em dia.</p>
                <div className="trc-explain__row">
                  <StatusPill status="done" /><StatusPill status="pending" /><StatusPill status="late" />
                </div>
              </section>
              <section className="trc-explain__card">
                <span className="trc-explain__num">03</span>
                <h3>Um plano até a data da prova</h3>
                <p>As horas são distribuídas por dia no calendário. Atrasou? O plano se reorganiza.</p>
                <div className="trc-explain__bars" aria-hidden="true">
                  {MINI_BARS.map(([h, c], i) => <span key={i} style={{ height: `${(h / 16) * 100}%`, background: c }} />)}
                </div>
              </section>
            </div>
            <div className="trc-seals">
              <strong>Toda fonte tem selo:</strong>
              {Object.keys(SEALS).map(k => (
                <span key={k}><SealPill seal={k} />{SEALS[k].short}</span>
              ))}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}

function CreateTrailModal({ open, onClose, onCreated }) {
  const [title, setTitle] = useState('');
  const [type, setType] = useState('certification');
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setTitle('');
      setType('certification');
      setError(null);
    }
  }, [open]);

  const submit = async (e) => {
    e?.preventDefault();
    setSaving(true);
    setError(null);
    try {
      const r = await trailService.create({ title, type });
      onCreated(r.slug);
    } catch (err) {
      setError(apiErrorMessage(err, 'Não foi possível criar a trilha.'));
      setSaving(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Nova trilha"
      footer={(
        <>
          <Button variant="ghost" onClick={onClose}>Cancelar</Button>
          <Button onClick={submit} disabled={saving || !title.trim()}>Criar e editar</Button>
        </>
      )}
    >
      <form onSubmit={submit} className="trc-create">
        <Field label="Nome do destino" required hint="Ex.: SAP Certified Professional – Developer – SAP Commerce Cloud">
          <input value={title} onChange={(e) => setTitle(e.target.value)} autoFocus maxLength={200} />
        </Field>
        <span className="ss-field__label">Tipo</span>
        <div className="trc-create__types">
          {Object.entries(TRAIL_TYPES).map(([k, t]) => (
            <button key={k} type="button" className={type === k ? 'is-active' : ''} aria-pressed={type === k} onClick={() => setType(k)}>
              <t.icon size={18} aria-hidden="true" /><strong>{t.label}</strong>
            </button>
          ))}
        </div>
        <p className="trc-create__note">
          <CircleDashed size={14} aria-hidden="true" /> A trilha nasce como rascunho, com as etapas básicas. Publique no editor.
        </p>
        {error && <p className="tr-error" role="alert">{error}</p>}
      </form>
    </Modal>
  );
}

export default Trails;
